import { LinkBuilder } from '../link-builder.service';

/**
* A helper service containing utility functions for interacting with a customer record object
*/
export class CustomerDetailsService {
  constructor(private $translate: ng.translate.ITranslateService, private linkBuilder: LinkBuilder) {
  }

  /**
   * Function to build a rich customer data object based on an existing customer record
   *
   * @param customer A customer record object
   *
   * @returns An object containing the customer's full name contact detail, primary addres and photo and any custom field data
   */
  public getEnhancedCustomerData(customer: InterACT.Interfaces.Customers.Customer) {
    const customerHelper: any = {};
    customerHelper.fullName = this.getFullName(customer);
    customerHelper.contactDetails = this.getContactDetails(customer);
    customerHelper.primaryAddress = this.getPrimaryAddress(customer);
    customerHelper.postalAddresses = this.getPostalAddresses(customer);
    customerHelper.primaryPhoto = this.getPrimaryPhoto(customer);
    customerHelper.customFields = this.getCustomFields(customer);
    return customerHelper;
  }

  /**
   * Builds and returns the passed in customer's full name.
   *
   * @param customer A customer record object
   *
   * @returns A string in the format of <b>Title Forename Surname</b>, e.g. Mr Jonathan Jones.
   */
  public getFullName(customer: InterACT.Interfaces.Customers.Customer) {
    const names = [];

    if (angular.isDefined(customer.title)) {
      names.push(this.$translate.instant('CUSTOMER_DETAIL.' + customer.title.toUpperCase()));
    }

    if (angular.isDefined(customer.forename)) {
      names.push(customer.forename);
    }

    if (angular.isDefined(customer.surname)) {
      names.push(customer.surname);
    }
    return names.join(' ');
  }

  /**
   * Determines if the passed in customer record contains a primary address
   *
   * @param customer A customer record object
   *
   * @returns an object containing the primary address if found, otherwise returns `undefined`
   */
  public getPrimaryAddress(customer: InterACT.Interfaces.Customers.Customer) {
    let primary = customer.addresses.filter(address => address.primary)[0];
    if (!primary) {
      primary = customer.addresses[0];
    }
    return primary;
  }

  public getPostalAddresses(customer: InterACT.Interfaces.Customers.Customer) {
    const postalAddresses = [];
    const hasPrimary: boolean = customer.addresses.filter(add => add.primary).length > 0
    for (let i = 0; i < customer.addresses.length; i++) {
      if (!customer.addresses[i].primary && (hasPrimary || i > 0)) {
        postalAddresses.push(customer.addresses[i]);
      }
    }
    return postalAddresses;
  }

  /**
   * Determines if the passed in customer record contains a primary photo
   *
   * @param customer A customer record object
   *
   * @returns An object containing the primary photo if found, otherwise returns `undefined`
   */
  public getPrimaryPhoto(customer: InterACT.Interfaces.Customers.Customer) {
    let primaryPhoto = undefined;
    angular.forEach(customer.photos, (photo) => {
      if (photo.primary === true) {
        primaryPhoto = photo;
        return primaryPhoto;
      }
    });
    return primaryPhoto;
  }

  /**
   * Builds an object to detail the available contact methods for the customer
   *
   * @param customer A customer record object
   *
   * @returns An object containing the available contact methods for the customer
   */
  public getContactDetails(customer: InterACT.Interfaces.Customers.Customer) {
    const contactDetails: any = {};
    contactDetails.emailAddresses = [];
    contactDetails.phoneNumbers = [];

    // Takes a copy of the cstomer object. Required to stop an issues with multiple calls
    const copyCust: InterACT.Interfaces.Customers.Customer = { ...customer };

    // Gets a primary feild
    const primaryContact = copyCust.contactDetails.filter(contact => contact.primary)[0];
    // If there is a primary then set else get the first phone number. If no phone number then first email.
    // It then removes then entry form the customer contact details so it will not be picked p
    // by following forEach
    if (primaryContact) {
      if (primaryContact.type === <any>'EMAIL') {
        contactDetails.primaryEmail = primaryContact;
      } else {
        contactDetails.primaryContactMethod = primaryContact;
      }
    }

    // Get an array of contact details that aren't primary
    copyCust.contactDetails.filter(contact => !contact.primary).forEach(contact => {
      switch (contact.type) {
        case <any>'EMAIL':
          (<any>contact).icon = 'email';
          contactDetails.emailAddresses.push(contact);
          break;
        case <any>'MOBILE':
          (<any>contact).icon = 'phone_iphone';
          contactDetails.phoneNumbers.push(contact);
          break;
        case <any>'HOME':
          (<any>contact).icon = 'call';
          contactDetails.phoneNumbers.push(contact);
          break;
        default:
      }
    })

    return contactDetails;
  }

  /**
   * Adds an address to the list of addresses for the customer
   *
   * @param customer A customer record object
   */
  public addAddress(customer: InterACT.Interfaces.Customers.Customer) {
    customer.addresses.push({
      primary: false,
      type: <any>'',
      status: 'VALID',
      line1: '',
      line2: '',
      line3: '',
      line4: '',
      line5: '',
      line6: '',
      postcode: '',
      link: ''
    });
  }

  /**
   * Adds a contact method to the list of contact methods for the customer
   *
   * @param customer A customer record object
   */
  public addContactDetail(customer: InterACT.Interfaces.Customers.Customer) {
    customer.contactDetails.push({
      primary: false,
      type: <any>'',
      detail: ''
    });
  }

  /**
   * Removes an address from the list of addresses for the customer
   *
   * @param customer A customer record object
   * @param address The address object to be deleted
   */
  public deleteAddress(customer: InterACT.Interfaces.Customers.Customer, address: InterACT.Interfaces.Customers.CustomerAddress) {
    if (customer.addresses.length > 1) {
      const index = customer.addresses.indexOf(address);
      customer.addresses.splice(index, 1);
    }
  }

  /**
   * Removes an address from the list of addresses for the customer
   *
   * @param customer A customer record object
   * @param contactDetail The contact details object to be deleted
   */
  public deleteContactDetail(customer: InterACT.Interfaces.Customers.Customer, contactDetail: InterACT.Interfaces.Customers.CustomerContactDetail) {
    if (customer.contactDetails.length > 1) {
      const index = customer.contactDetails.indexOf(contactDetail);
      customer.contactDetails.splice(index, 1);
    }
  }

  /**
   * Adds schema field information to the customer record object
   *
   * @param customer A customer record object
   * @param schemaFields A schemaFields object
   */
  public addSchemaFields(customer: InterACT.Interfaces.Customers.Customer, schemaFields: any) {
    customer.fields = <any>{};
    customer.fields.schema = <any>{};
    customer.fields.schema.name = schemaFields.name;
    customer.fields.schema.link = this.linkBuilder.getCustomerSchemasLink(schemaFields.type);
    customer.fields.customData = schemaFields.fields;
  }

  /**
   * Retrieves any custom fields set against the customer record.
   *
   * @param customer A customer record object
   *
   * @returns An object containing any custom fields for the customer if originally set.
   */
  public getCustomFields(customer: InterACT.Interfaces.Customers.Customer) {
    if (angular.isDefined(customer.fields)) {
      return (<any>customer.fields).data;
    }
  }

  /**
   * Removes schema field information if present from the customer record object
   *
   * @param customer A customer record object
   */
  public clearSchemaFields(customer: InterACT.Interfaces.Customers.Customer) {
    if (customer.hasOwnProperty('fields')) {
      delete customer.fields;
    }
  }
}
